import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
type Language = 'en' | 'id';
type ThemeStyle = 'default' | 'minimalist';
type UITheme = 'professional' | 'glassmorphism' | 'brutalist';
type SidebarType = 'circular' | 'compact';

interface SettingsContextType {
  theme: Theme;
  language: Language;
  themeStyle: ThemeStyle;
  uiTheme: UITheme;
  sidebarType: SidebarType;
  emailNotifications: boolean;
  pushNotifications: boolean;
  projectUpdates: boolean;
  taskReminders: boolean;
  
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  setThemeStyle: (style: ThemeStyle) => void;
  setUITheme: (theme: UITheme) => void;
  setSidebarType: (type: SidebarType) => void;
  setEmailNotifications: (enabled: boolean) => void;
  setPushNotifications: (enabled: boolean) => void;
  setProjectUpdates: (enabled: boolean) => void;
  setTaskReminders: (enabled: boolean) => void;
}

const defaultSettings: Omit<SettingsContextType, 
  'setTheme' | 'setLanguage' | 'setThemeStyle' | 'setUITheme' | 'setSidebarType' |
  'setEmailNotifications' | 'setPushNotifications' | 
  'setProjectUpdates' | 'setTaskReminders'> = {
  theme: 'light',
  language: 'en',
  themeStyle: 'default',
  uiTheme: 'professional',
  sidebarType: 'circular',
  emailNotifications: false,
  pushNotifications: true,
  projectUpdates: true,
  taskReminders: true,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    try {
    const savedSettings = localStorage.getItem('app-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        // Validate language
        if (!['en', 'id'].includes(parsed.language)) {
          parsed.language = 'en';
        }
        // Validate themeStyle
        if (!['default', 'minimalist'].includes(parsed.themeStyle)) {
          parsed.themeStyle = 'default';
        }
        // Validate uiTheme
        if (!['professional', 'glassmorphism', 'brutalist'].includes(parsed.uiTheme)) {
          parsed.uiTheme = 'professional';
        }
        return parsed;
      }
      return defaultSettings;
    } catch (error) {
      console.error('Error loading settings:', error);
      return defaultSettings;
    }
  });

  useEffect(() => {
    try {
    localStorage.setItem('app-settings', JSON.stringify(settings));
    
    // Apply theme to document
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      }

      // Apply theme style
      if (settings.themeStyle === 'minimalist') {
        document.documentElement.classList.add('minimalist');
      } else {
        document.documentElement.classList.remove('minimalist');
      }

      // Apply UI theme
      document.documentElement.classList.remove('theme-glassmorphism', 'theme-brutalist');
      if (settings.uiTheme === 'glassmorphism') {
        document.documentElement.classList.add('theme-glassmorphism');
      } else if (settings.uiTheme === 'brutalist') {
        document.documentElement.classList.add('theme-brutalist');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  }, [settings]);

  const updateSetting = <K extends keyof typeof settings>(key: K, value: typeof settings[K]) => {
    try {
    setSettings(prev => ({
      ...prev,
        [String(key)]: value
    }));
    } catch (error) {
      console.error(`Error updating setting ${String(key)}:`, error);
    }
  };

  return (
    <SettingsContext.Provider value={{
      ...settings,
      setTheme: (theme) => updateSetting('theme', theme),
      setLanguage: (language) => updateSetting('language', language),
      setThemeStyle: (style) => updateSetting('themeStyle', style),
      setUITheme: (theme) => updateSetting('uiTheme', theme),
      setSidebarType: (type) => updateSetting('sidebarType', type),
      setEmailNotifications: (enabled) => updateSetting('emailNotifications', enabled),
      setPushNotifications: (enabled) => updateSetting('pushNotifications', enabled),
      setProjectUpdates: (enabled) => updateSetting('projectUpdates', enabled),
      setTaskReminders: (enabled) => updateSetting('taskReminders', enabled),
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
